<template>
  <div>
    <div>Pontuação total da POEM (Máximo de 28)</div>
    <div
      class="d-flex mt-4 flex-row"
      :class="{
        'flex-column gap-8': $vuetify.breakpoint.smAndDown,
        'gap-12': !$vuetify.breakpoint.smAndDown,
      }"
    >
      <div class="d-flex gap-2 mt-2 align-center">
        <v-text-field
          :disabled="disabled"
          outlined
          dense
          :min="0"
          :max="28"
          type="number"
          v-if="form.poem.resultado_manual"
          v-model="form.poem.value"
          :rules="[$validation.minValue(0), $validation.maxValue(28)]"
          hide-spin-buttons
        />
        <span v-else :class="form.poem.value === '?' ? 'black--text text-h6' : `big-number ${poemSeverity.class}`">
          {{ form.poem.value ? form.poem.value : '?' }}
        </span>
        <span v-if="form.poem.value" class="ml-2" :class="poemSeverity.class">
          {{ poemSeverity.text }}
        </span>
      </div>
    </div>
    <v-switch
      class="my-4"
      label="Aplicar resultado manualmente"
      :disabled="disabled"
      v-model="form.poem.resultado_manual"
      hide-details
    />
    <div class="mb-4 mt-10">
      <send-attendance-survey survey-type="poem" :disabled="disabled"></send-attendance-survey>
    </div>
    <template v-if="!form.poem.resultado_manual">
      <div v-for="(question, index) in poemQuestions.questions" :key="question.key">
        <div class="d-flex">
          <p class="list-number mb-0">{{ index + 1 }}.</p>
          <p class="question mb-0">{{ question.name }}</p>
        </div>
        <v-radio-group
          class="px-3"
          :disabled="disabled"
          v-model="form.poem.answers[question.key].value"
          :row="!$vuetify.breakpoint.smAndDown"
        >
          <v-radio
            v-for="option in poemQuestions.options"
            :key="`${question.key}-${option.key}`"
            :value="option.value"
            :label="option.label"
          />
        </v-radio-group>
      </div>
    </template>
    <div class="reference mt-4">
      <div>Referências:</div>
      <ul>
        <li>
          Charman CR, Venn AJ, Williams HC. The Patient-Oriented Eczema Measure: Development and Initial Validation of a
          New Tool for Measuring Atopic Eczema Severity From the Patients' Perspective. Arch Dermatol.<br />
          2004;140:1513-1519
        </li>
        <li>
          Charman CR, Venn AJ, Ravenscroft JC, Williams HC. Translating Patient-Oriented Eczema Measure (POEM) scores
          into clinical practice by suggesting severity strata derived using anchor-based methods. Br J Dermatol. <br />
          Dec 2013; 169(6): 1326–1332.
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import { POEM_QUESTIONS } from '@/components/AttendanceForm/atopicdermatitis/constants/poemquestions'
import { createNamespacedHelpers } from 'vuex'
import SendAttendanceSurvey from '@/components/SendAttendanceSurvey.vue'

const {
  mapMutations: newAttendanceFormMutations,
  mapActions: newAttendanceFormActions,
  mapGetters: newAttendanceFormGetters,
} = createNamespacedHelpers('newAttendanceForm')

export default {
  name: 'PoemFormNode',
  components: { SendAttendanceSurvey },
  async created() {
    const extraAnswer = this.extraAnswers['poem']
    if (extraAnswer) {
      this.form = extraAnswer
      return
    }

    const surveyId = this.extraAnswers['survey']?.poem?.uuid
    if (surveyId) {
      const survey = await this.$api.getSurveyByUuid(surveyId)
      const keys = survey.data ? Object.keys(survey.data) : []
      for (const key of keys) {
        if (key === 'done' || key === 'startedAt') {
          continue
        }
        this.form.poem.answers[key].value = survey.data[key].value
      }
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        poem: {
          value: null,
          resultado_manual: false,
          answers: {
            coceira: { value: null },
            sono: { value: null },
            sangramento: { value: null },
            vazamento: { value: null },
            rachaduras: { value: null },
            descamacao: { value: null },
            secura: { value: null },
          },
        },
      },
    }
  },
  methods: {
    ...newAttendanceFormMutations(['updateExtraAnswer']),
    ...newAttendanceFormActions(['getParameter', 'getExtraAnswer']),
  },
  computed: {
    ...newAttendanceFormGetters(['parameters', 'extraAnswers']),
    poemQuestions() {
      return POEM_QUESTIONS
    },
    poemSeverity() {
      if (isNaN(this.computedPoem)) {
        return { class: 'grey--text', text: '' }
      } else if (this.computedPoem <= 2) {
        return { class: 'cyan--text', text: 'Praticamente sem lesões' }
      } else if (this.computedPoem <= 7) {
        return { class: 'green--text', text: 'Leve' }
      } else if (this.computedPoem <= 16) {
        return { class: 'orange--text', text: 'Moderado' }
      } else if (this.computedPoem <= 24) {
        return { class: 'orange--text text--darken-4', text: 'Severo' }
      } else {
        return { class: 'red--text text--darken-2', text: 'Muito severo' }
      }
    },
    computedPoem() {
      if (this.form.poem.resultado_manual) {
        return this.form.poem.value || 0
      }
      const answers = []
      Object.keys(this.form.poem.answers).forEach((answer) => {
        answers.push(this.form.poem.answers[answer].value)
      })
      return answers.reduce((partialSum, a) => partialSum + a, 0)
    },
  },
  watch: {
    computedPoem(newVal) {
      if (this.form.poem.resultado_manual) {
        return
      }
      this.form.poem.value = Number(newVal).toFixed(2)
    },
    form: {
      handler(value) {
        this.updateExtraAnswer({ name: 'poem', value })
      },
      deep: true,
    },
  },
}
</script>
<style scoped>
.big-number {
  font-weight: 500;
  color: green;
  font-size: 2.5rem;
}

.reference {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.question {
  color: var(--v-neutral-lighten17);
  font-size: 16px;
  letter-spacing: 0.5px;
}

.list-number {
  letter-spacing: 0.15px;
  font-size: 16px;
  font-weight: 500;
  color: #000000;
  margin-right: 8px;
}
</style>
